/**
 * Мягкое пятно света, следующее за курсором.
 *
 * Координаты пишутся в переменные CSS, а рисует всё браузер: так при
 * движении мыши не перерисовывается ни один компонент React. Обновление
 * привязано к кадру отрисовки — события мыши приходят чаще, чем экран
 * успевает обновиться, и без этого работа была бы напрасной.
 */

import { useEffect, type ReactNode } from 'react';

export function CursorGlow(): ReactNode {
  useEffect(() => {
    const hasMouse = window.matchMedia('(hover: hover) and (pointer: fine)');
    const prefersCalm = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!hasMouse.matches || prefersCalm.matches) {
      return;
    }

    const root = document.documentElement;
    let frame = 0;
    let x = 0;
    let y = 0;

    const apply = (): void => {
      frame = 0;
      root.style.setProperty('--cursor-x', `${String(x)}px`);
      root.style.setProperty('--cursor-y', `${String(y)}px`);
    };

    const onMove = (event: PointerEvent): void => {
      x = event.clientX;
      y = event.clientY;
      if (frame === 0) {
        frame = window.requestAnimationFrame(apply);
      }
    };

    // Класс включает свечение только после первого движения мыши:
    // иначе при загрузке пятно висело бы в углу экрана.
    const onFirstMove = (event: PointerEvent): void => {
      root.classList.add('has-cursor-glow');
      onMove(event);
    };

    window.addEventListener('pointermove', onFirstMove, { once: true, passive: true });
    window.addEventListener('pointermove', onMove, { passive: true });

    return () => {
      window.removeEventListener('pointermove', onFirstMove);
      window.removeEventListener('pointermove', onMove);
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }

      root.classList.remove('has-cursor-glow');
    };
  }, []);

  return <div className="cursor-glow" aria-hidden="true" />;
}
